import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ShieldCheck, ShieldX, Search, Loader2, Award } from "lucide-react";

export default function VerifyCertificate() {
  const { code: codeParam } = useParams();
  const navigate = useNavigate();
  const [code, setCode] = useState(codeParam || "");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const check = async (c) => {
    const value = (c || code).trim().toUpperCase();
    if (!value) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/verify/${value}`);
      setResult(data);
    } catch {
      setResult({ valid: false });
    } finally { setLoading(false); }
  };

  useEffect(() => { if (codeParam) check(codeParam); /* eslint-disable-next-line */ }, [codeParam]);

  return (
    <div className="App">
      <Navbar />
      <section className="pt-32 pb-20 max-w-2xl mx-auto px-6">
        <div className="text-center mb-10">
          <div className="w-14 h-14 rounded-md bg-[#00E5FF]/15 flex items-center justify-center mx-auto mb-5"><Award className="w-7 h-7 text-[#00E5FF]" /></div>
          <span className="font-mono text-xs uppercase tracking-widest text-[#00E5FF]">// Verificación</span>
          <h1 className="font-heading font-bold text-4xl md:text-5xl tracking-tighter mt-3 mb-3">Verificar certificado</h1>
          <p className="font-body text-[#8A8D98]">Ingresa el código de verificación que aparece en el certificado.</p>
        </div>

        <div className="flex gap-3 mb-10">
          <Input data-testid="verify-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === "Enter" && check()} placeholder="CP-XXXX-XXXX"
            className="bg-[#13141C] border-white/10 rounded-md focus:border-[#00E5FF] focus-visible:ring-1 focus-visible:ring-[#00E5FF] text-white h-12 font-mono text-center tracking-widest" />
          <Button data-testid="verify-btn" onClick={() => check()} disabled={loading} className="bg-[#00E5FF] text-black font-semibold hover:bg-white rounded-md h-12 px-6 shrink-0">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Search className="w-4 h-4 mr-2" /> Verificar</>}
          </Button>
        </div>

        {result && (
          result.valid ? (
            <div data-testid="verify-valid" className="bg-[#0C0D12] border border-[#00E6BA]/30 rounded-xl p-8">
              <div className="flex items-center gap-3 mb-6">
                <ShieldCheck className="w-7 h-7 text-[#00E6BA]" />
                <span className="font-heading font-bold text-xl text-[#00E6BA]">Certificado válido</span>
              </div>
              <dl className="space-y-4">
                {[
                  ["Alumno", result.student_name],
                  ["Curso", result.course_title],
                  ["Instructor", result.instructor_name],
                  ["Código", result.code],
                  ["Emitido", new Date(result.issued_at).toLocaleDateString("es-PE")],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b border-white/5 pb-3">
                    <dt className="font-mono text-xs uppercase tracking-wider text-[#525560]">{k}</dt>
                    <dd className="font-body text-sm text-white text-right max-w-[60%]">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ) : (
            <div data-testid="verify-invalid" className="bg-[#0C0D12] border border-destructive/30 rounded-xl p-8 text-center">
              <ShieldX className="w-10 h-10 text-destructive mx-auto mb-4" />
              <p className="font-heading font-bold text-xl mb-1">Certificado no encontrado</p>
              <p className="font-body text-sm text-[#8A8D98]">El código ingresado no corresponde a ningún certificado emitido.</p>
            </div>
          )
        )}

        <div className="text-center mt-10">
          <Button variant="ghost" onClick={() => navigate("/")} className="text-[#8A8D98] hover:text-white hover:bg-white/5">Volver al inicio</Button>
        </div>
      </section>
      <Footer />
    </div>
  );
}
